import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
type Size = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-1.5 rounded-md font-semibold transition active:translate-y-px disabled:pointer-events-none disabled:opacity-50';
const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
};
const variants: Record<Variant, string> = {
  primary: 'bg-blue text-white shadow-premium-soft hover:brightness-110',
  secondary:
    'border border-surface-line bg-white text-neutral-ink hover:border-blue/40 hover:text-blue',
  ghost: 'text-neutral-ink/80 hover:bg-blue-10 hover:text-blue',
  danger:
    'border border-orange/40 bg-white text-orange hover:bg-orange/5 hover:border-orange',
  accent: 'bg-orange text-white shadow-premium-soft hover:brightness-110',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    leftIcon,
    rightIcon,
    children,
    className,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(base, sizes[size], variants[variant], className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : leftIcon}
      {children}
      {rightIcon}
    </button>
  );
});
